import { Organization } from '@lucidtech/las-sdk-browser';
import { createFileRoute } from '@tanstack/react-router';
import { fallback, zodValidator } from '@tanstack/zod-adapter';
import z from 'zod';

import { ONBOARDING_COMPLETED_SESSION_KEY } from '@/constants';
import { New } from '@/features/organizations';
import { OrganizationProvider } from '@/store';
import { createClientFromAuth } from '@/utils';

const NewOrganizationSearchSchema = z.object({
  onboarding: fallback(z.optional(z.boolean()), undefined),
});

const isOnboardingOrg = (org: Organization) => {
  const createdTime = org.createdTime;
  const timeDelta = new Date().getTime() - createdTime.getTime();
  return timeDelta < 60 * 60 * 1000 && !org.url;
};

export const Route = createFileRoute('/_auth/organizations/new')({
  validateSearch: zodValidator(NewOrganizationSearchSchema),
  loaderDeps: ({ search: { onboarding } }) => ({ onboarding }),
  loader: async ({ context: { client }, deps: { onboarding } }) => {
    let newOrganization: Organization | null = null;

    if (onboarding) {
      const { organizations } = await client.listOrganizations();

      if (organizations.length === 0) {
        newOrganization = await client.createOrganization({ useNewScopes: true });
      } else if (organizations.length === 1) {
        const existingOrg = organizations[0];

        if (isOnboardingOrg(existingOrg)) {
          newOrganization = existingOrg;
        }
      }
    }

    const createOrScheduleUpdateOrganization = async ({ name, url }: { name?: string; url?: string }) => {
      if (newOrganization != null) {
        localStorage.setItem(ONBOARDING_COMPLETED_SESSION_KEY, JSON.stringify({ name, url }));
        return newOrganization;
      }

      return await client.createOrganization({ name, url, useNewScopes: true });
    };

    return { createOrScheduleUpdateOrganization };
  },
  component: () => (
    <OrganizationProvider>
      <New />
    </OrganizationProvider>
  ),
});
